import type {
  AmendmentSignalName,
  ContractTypeAutoApprovalDisposition,
  ContractTypeAutoApprovalNotEligible,
  ContractTypeAutoApprovalPauseKind,
  ContractTypeAutoApprovalState,
  ContractTypeAutoConfirmationKind,
  ContractTypeSwitchOnRefusal,
  ContractTypeConfirmation,
  ContractTypeDecidedBy,
  ContractTypeDecisionKind,
  ContractTypeDecisionNoteKind,
  ContractTypeNameEvidence,
  ContractTypeProposalsResponse,
  ContractTypeProvisionalReason,
  ContractTypeRuleHold,
} from '@clm/shared-types';

/**
 * CLM-110, CLM-118 — the words for a contract's type: how it was decided
 * (DR-10), why it is not settled yet (R-110-13, R-118-15), what an Admin's
 * decision did (R-110-17, R-118-10), what settled it with no person (D-110-3,
 * 1a), what the file name gave (R-118-3), why the AI's type was held (R-118-9)
 * and what a decision flags but never changes (§3.5). Shared by the Type
 * proposals page, the decision panel, the Contract record, Needs Attention and
 * the type tag so no two surfaces describe the same fact differently. Kept out
 * of the component files so fast refresh stays whole (the
 * `pipeline-status-chips.ts` pattern).
 */
export const CONTRACT_TYPE_SOURCE_COPY: Record<ContractTypeDecidedBy | 'unrecorded', string> = {
  /* Every deterministic intake rule (R-110-14): a file-name or folder token, a SOW number, the relationship, or OTHER. */
  derived: 'from the file name, folder or filing rules',
  manual: 'chosen by a person',
  ai_proposed: 'read from the document by the AI and confirmed by an administrator',
  /* R-110-15 — an amendment's type is its agreement's, held equal by the schema. */
  inherited: 'follows the agreement it amends',
  unrecorded: 'not recorded (before CLM-110)',
};

/**
 * The words for how a type was decided, `null` being a row recorded before
 * CLM-110. CLM-118: with the confirmation, two cases read differently — a Type
 * pending placeholder came from no rule at all, and the AI's type applied as the
 * working type (case 2, `ai_proposed` while provisional) is not yet verified.
 * CLM-121 (R-121-14): the AI's type approved by policy (`ai_proposed` +
 * `auto_confirmed`) was checked by no person, and says so.
 */
export function contractTypeSourceWords(
  source: ContractTypeDecidedBy | null,
  confirmation?: ContractTypeConfirmation,
): string {
  if (confirmation === 'pending') return 'no type yet: the file name gave none, or gave more than one';
  if (source === 'ai_proposed' && confirmation === 'auto_confirmed') {
    return 'read from the document by the AI, because the file name gave no type; approved by policy — no person has checked it';
  }
  if (source === 'ai_proposed' && confirmation !== undefined && confirmation !== 'confirmed') {
    return 'read from the document by the AI, because the file name gave no type it could trust; an administrator verifies it';
  }
  return source === null ? CONTRACT_TYPE_SOURCE_COPY.unrecorded : CONTRACT_TYPE_SOURCE_COPY[source];
}

/**
 * The `provisional` tag's title — one sentence on every surface that shows a
 * type (R-110-23, R-118-12): a provisional type that looks settled is
 * D-2.1-4's failure, applied to the type. CLM-121 (R-121-14): the policy is the
 * other way a type settles with no administrator.
 */
export const PROVISIONAL_TAG_TITLE =
  'Not yet confirmed. An administrator confirms it unless the file name and the AI agree, or auto-approval for AI-decided types approves it.';

/** CLM-118 (R-118-12, B19), CLM-121 — the "Type pending" tag's title: no type word is printed, because there is none. */
export const PENDING_TAG_TITLE =
  'No type yet. The file name gave none, or gave conflicting ones; the AI reads the document and an administrator verifies the type, unless auto-approval for AI-decided types approves it.';

/**
 * CLM-118 (R-118-12, default 3, B8) — the "file name and AI agree" tag's title.
 * A type nobody checked never reads as an administrator's.
 */
export const AUTO_CONFIRMED_TAG_TITLE =
  'Confirmed with no administrator step: the file name and the AI’s reading of the document gave the same type. No person has checked it.';

/** CLM-121 (R-121-14, default 3) — the "approved by policy" tag's title. Never an administrator's. */
export const AUTO_APPROVED_BY_POLICY_TAG_TITLE =
  'Approved with no administrator step: auto-approval for AI-decided types accepted the AI’s reading, because its measured accuracy is above 0.900. No person has checked it.';

/**
 * CLM-121 (R-121-14) — the "confirmed automatically" tag's title, on a surface
 * that does not know which kind settled the type: it names both causes, and no
 * administrator.
 */
export const AUTO_CONFIRMED_ANY_TAG_TITLE =
  'Confirmed with no administrator step: either the file name and the AI’s reading of the document gave the same type, or auto-approval for AI-decided types approved the AI’s reading. No person has checked it.';

/** Why a type is not settled yet — the API's computed cause, in words (R-110-13, R-110-28, R-118-15). */
export const PROVISIONAL_REASON_COPY: Readonly<Record<ContractTypeProvisionalReason, string>> = {
  awaiting_ai: 'waiting for the AI to read the document',
  ai_agrees: 'the AI read the document as this type; an administrator has not confirmed it yet',
  ai_disagrees: 'the AI read the document as another type; an administrator decides',
  ai_no_type: 'the AI read the document but gave no usable type; an administrator decides',
  restricted: 'processing-restricted, so never read by an AI (§4C); an administrator confirms it',
  no_ai_proposal: 'not read by the AI (filed before the AI read types, or its reading failed); an administrator confirms it',
  ai_sow_needs_number:
    'the AI read the document as a SOW; a SOW is never typed without its number, so an administrator adds it',
  ai_type_not_applied: 'the AI read the document, but its type was not applied; an administrator decides',
  awaiting_rerun: 'its reading failed; waiting for a re-run or an alias decision, and an administrator may decide it now',
};

/** What an Admin's decision did, as its `HumanVerification` row records it (R-110-17, R-118-10). */
export const DECISION_KIND_COPY: Readonly<Record<ContractTypeDecisionKind, string>> = {
  accepted_ai: 'accepted the AI’s reading',
  kept_against_ai: 'kept the type against the AI’s reading',
  corrected: 'corrected the type',
  confirmed: 'confirmed the type, with no AI reading behind it',
  typed: 'typed it: it was Type pending, and the type set is not the AI’s reading',
};

/** CLM-118 (D-110-3), CLM-121 (D-110-4) — what settled a type with no administrator; never a decision (B8). */
export const AUTO_CONFIRMATION_KIND_COPY: Readonly<Record<ContractTypeAutoConfirmationKind, string>> = {
  file_name_and_ai_agree: 'the file name and the AI agree',
  auto_approval_policy: 'approved by policy — auto-approval for AI-decided types',
};

/** CLM-121 (R-121-14) — the short tag word for each kind, and for a surface that does not know the kind. */
export const AUTO_CONFIRMATION_TAG_COPY: Readonly<Record<ContractTypeAutoConfirmationKind | 'unknown', string>> = {
  file_name_and_ai_agree: 'file name and AI agree',
  auto_approval_policy: 'approved by policy',
  unknown: 'confirmed automatically',
};

/** CLM-121 (R-121-15) — the safety sample, wherever an Admin meets one. */
export const SAFETY_SAMPLE_TAG = 'Safety sample (1 in 5)';
export const SAFETY_SAMPLE_TAG_TITLE =
  'While auto-approval for AI-decided types is on, 1 in 5 of the types it could approve still comes to an administrator, chosen by a fixed rule. Check it as usual; correcting it pauses auto-approval.';
export const SAFETY_SAMPLE_LINE =
  'A safety sample: 1 in 5 types the AI decides go to an administrator while auto-approval is on. Check it as usual; correcting it pauses auto-approval.';

/** CLM-121 (R-121-9) — the decision dialog's warning before a correction that pauses auto-approval. */
export const PAUSE_WARNING_COPY: Readonly<Record<'sample' | 'policy_approval', string>> = {
  sample:
    'This type is an auto-approval safety sample. Correcting it pauses auto-approval for AI-decided types until an administrator switches it on again.',
  policy_approval:
    'This type was approved by policy. Correcting it pauses auto-approval for AI-decided types until an administrator switches it on again.',
};

/** CLM-121 (§3.4) — why an AI-decided type is never approved by policy. */
export const AUTO_APPROVAL_NOT_ELIGIBLE_COPY: Readonly<Record<ContractTypeAutoApprovalNotEligible, string>> = {
  ai_type_other: 'the AI read it as OTHER; only MSAs and NDAs are approved by policy',
  conflicting_names: 'its file name names more than one type',
  name_typed: 'the file name gave a type, so this is not a type the file name left open',
  amendment_signal: 'it is flagged as possibly an amendment',
  vendor_party: 'it is a vendor’s contract',
};

/** CLM-121 (R-121-11) — what the type step did with an AI-decided type, as its act row records it. */
export const AUTO_APPROVAL_DISPOSITION_COPY: Readonly<Record<ContractTypeAutoApprovalDisposition, string>> = {
  auto_approved: 'approved by policy',
  sampled: 'sent to an administrator as the 1-in-5 safety sample',
  not_eligible: 'sent to an administrator: never approved by policy',
  switched_off: 'sent to an administrator: auto-approval is off',
  lapsed: 'sent to an administrator: auto-approval lapsed on a new model or schema version',
  paused_by_correction: 'sent to an administrator: auto-approval is paused by a correction',
  too_few_decisions: 'sent to an administrator: fewer than 30 counted decisions',
  stale: 'sent to an administrator: no counted check for 90 days',
  below_threshold: 'sent to an administrator: the counted accuracy is 0.900 or below',
};

/** CLM-121 (§3.5) — the switch's state, as the Auto-approval page names it. */
export const AUTO_APPROVAL_STATE_COPY: Readonly<Record<ContractTypeAutoApprovalState, string>> = {
  on: 'On — AI-decided MSAs and NDAs are approved by policy; 1 in 5 still goes to an administrator',
  switched_off: 'Off — every AI-decided type goes to an administrator',
  not_read: 'On, but the AI has not read a contract type yet',
  lapsed: 'Lapsed — a new model or schema version; switch it on again once its figure qualifies',
  paused_by_correction: 'Paused — an administrator corrected a type; switch it on again to resume',
  too_few_decisions: 'Held — fewer than 30 counted decisions at the current model and schema version',
  stale: 'Paused — no counted check for 90 days; it resumes on the next one',
  below_threshold: 'Stopped — the counted accuracy is 0.900 or below; it resumes when the figure recovers',
};

/** CLM-121 (R-121-9) — what paused the switch. */
export const AUTO_APPROVAL_PAUSE_COPY: Readonly<Record<ContractTypeAutoApprovalPauseKind, string>> = {
  corrected_sample: 'a safety sample',
  corrected_policy_approval: 'a type approved by policy',
};

/** CLM-121 (R-121-13) — why Switch on is not offered now. */
export const SWITCH_ON_REFUSAL_COPY: Readonly<Record<ContractTypeSwitchOnRefusal, string>> = {
  no_reading_grain: 'The AI has not read a contract type yet, so there is no figure to switch on at.',
  too_few_decisions: 'Too few decisions: switching on needs at least 30 counted decisions at the current model and schema version.',
  not_above_threshold: 'The counted accuracy is 0.900 or below; switching on needs it strictly above 0.900.',
  no_change: 'Auto-approval is already on at the current model and schema version, and neither paused nor lapsed.',
};

/** CLM-118 (R-118-3) — what the deterministic file-name rule found at intake. */
export const NAME_EVIDENCE_COPY: Readonly<Record<ContractTypeNameEvidence | 'unrecorded', string>> = {
  typed: 'the file name gives the type',
  untyped: 'the file name gives no type',
  conflicting: 'the file name names more than one type',
  unrecorded: 'not recorded (filed before CLM-118)',
};

export function nameEvidenceWords(evidence: ContractTypeNameEvidence | null): string {
  return evidence === null ? NAME_EVIDENCE_COPY.unrecorded : NAME_EVIDENCE_COPY[evidence];
}

/** CLM-118 (R-118-14) — the amendment word a file or folder name carries, as the flag names it. */
export const AMENDMENT_SIGNAL_COPY: Readonly<Record<AmendmentSignalName, string>> = {
  amendment: 'the name says Amendment',
  addendum: 'the name says Addendum',
  change_order: 'the name says Change Order',
  change_request: 'the name says Change Request',
  variation: 'the name says Variation',
  extension: 'the name says Extension',
  cr_sow_number: 'the name carries a change-request SOW number (…CR)',
};

/**
 * CLM-118 (R-118-14) — why a contract is flagged "possibly an amendment", in
 * words: the name's word, then what the AI's reading refers to.
 */
export function amendmentSignalWords(signal: {
  readonly name_word: AmendmentSignalName | null;
  readonly amends_reference: string | null;
  readonly supersedes_reference: string | null;
}): string {
  const words: string[] = [];
  if (signal.name_word !== null) words.push(AMENDMENT_SIGNAL_COPY[signal.name_word]);
  if (signal.amends_reference !== null) words.push(`the document refers to “${signal.amends_reference}”`);
  if (signal.supersedes_reference !== null) words.push(`the document refers to “${signal.supersedes_reference}”`);
  return words.join('; ');
}

/**
 * CLM-118 (R-118-9, B16) — why the AI's type was not applied to an agreement
 * with no trusted file-name type. `relationship_clash` names the link; the
 * caller passes it, or `null` when the clash is with contracts filed under it.
 */
export function aiTypeHeldWords(hold: ContractTypeRuleHold, linkedKey: string | null): string {
  switch (hold) {
    case 'typed_key':
      return 'the ID names a type, filed before IDs stopped carrying it';
    case 'sow_number_recorded':
      return 'its SOW number was recorded at filing';
    case 'relationship_clash':
      return linkedKey === null
        ? 'it would break the link to the contracts filed under it'
        : `it would break the link to ${linkedKey}`;
    case 'superseded':
      return 'its family has a superseded contract';
    case 'not_replaceable':
      return 'where its type came from was not recorded when it was filed';
  }
}

/** What a decision flags but never changes (§3.5), and what the record's lineage notes say. */
export const DECISION_NOTE_COPY: Readonly<Record<ContractTypeDecisionNoteKind, string>> = {
  children_under_non_msa: 'Contracts sit under this one although it is not an MSA. They are not changed.',
  msa_under_parent: 'This MSA itself sits under another contract. That edge is not changed.',
  supersession_type_differs:
    'A supersession edge joins contracts whose types now differ. Each contract in the chain is decided on its own, from its own record.',
  sow_numbers_cleared: 'SOW numbers were cleared. The SOW reconciliation will now report a listed number as missing.',
  deviation_reports_kept: 'Deviation reports already run are kept: they are immutable and pinned to the type they ran under.',
  past_base_nominations: 'Past base nominations are kept; the Base MSA page reports them as a type mismatch.',
};

/** R-110-25 — why an old key's middle segment differs from the type. */
export function legacyKeyCopy(segment: string, type: string): string {
  return `Issued as a ${segment} ID before IDs stopped carrying the type; the type is ${type}. IDs never change.`;
}

export const TYPE_PROPOSALS_FORBIDDEN_COPY =
  'Type decisions are for administrators (admin:configure). Only an administrator confirms or corrects a contract’s ' +
  'type (D-110-2); the type itself shows on every contract, with its provisional tag until then.';

/** On a `409 stale_view`: the decision applied only from the state it named, so nothing was written. */
export const DECISION_STALE_COPY = 'This contract changed since you opened the page — reload.';

/**
 * CLM-118 (default 9) — the typing guide, for documents the file name and the
 * vocabulary do not settle on their own. Shown once, collapsible, on Type
 * proposals.
 *
 * CLM-119 (R-119-21) appends three lines; the first four are unchanged. The AI's
 * type instruction now carries this guide (1.6.0), but not the vendor rule:
 * D-110-4 point 2 types a vendor's SOW OTHER, and telling buyer from supplier is
 * left to the Admin until vendor contracts get their own rule (R-119-3).
 */
export const TYPING_GUIDE: readonly string[] = [
  'An order form or a work order is a SOW.',
  'A PO, an LOI, an MoU or a partner agreement is OTHER.',
  'A DPA is an amendment of its MSA if it says so; otherwise it is OTHER.',
  'Anything that is not a contract is OTHER.',
  'A combined MSA and SOW, or a self-contained services agreement, is a SOW if it carries a SOW number; otherwise it is an MSA.',
  'A teaming agreement is OTHER; a BAA is read like a DPA.',
  'A vendor’s SOW, where cloudEQ buys the services, is OTHER until vendor contracts get their own rule.',
];

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** The seven lists of the Type proposals page (R-110-22, R-118-16). */
export type TypeProposalsTab = 'disagrees' | 'ai_decided' | 'pending' | 'agrees' | 'not_read' | 'newer' | 'amendments';

/**
 * What an empty list on the Type proposals page means, read from the totals —
 * never a blanket "every proposal matches". No AI reading at all is the state
 * right after a deploy: nothing re-reads a contract that was extracted before
 * the AI read types (§2), so the copy says where those contracts are listed
 * instead of promising a reading "when it is next extracted". Proposals on
 * amendments are shown under their agreement and never compared (R-110-18), and
 * neither is a reading of a Type pending placeholder (R-118-16).
 */
export function typeProposalsEmptyCopy(tab: TypeProposalsTab, totals: ContractTypeProposalsResponse['totals']): string {
  const noReadings =
    'No AI readings yet. Contracts extracted from this release on are read by the AI; earlier ones are under Not read by AI.';
  const amendments =
    totals.not_compared === 0
      ? ''
      : ` ${plural(totals.not_compared, 'reading is', 'readings are')} on amendments or Type pending contracts, never compared with a type on their own.`;
  switch (tab) {
    case 'disagrees':
      return totals.proposals === 0
        ? noReadings
        : `Nothing to decide here: no undecided agreement has an AI reading that disagrees with its type.${amendments}`;
    case 'ai_decided':
      return 'Nothing to verify here: the AI has not supplied the type of any contract whose file name gave none.';
    case 'pending':
      /* `totals.pending` counts only what an Admin can decide now: one still waiting for the AI is under
       * `awaiting_ai` (409 awaiting_ai_proposal), so an empty tab promises nothing about those. */
      return totals.awaiting_ai === 0
        ? 'No contract is Type pending: every agreement in your scope has a type from its file name, a person or the AI.'
        : 'No Type pending contract is waiting for an administrator. Any still waiting for the AI to read them are counted under Waiting for the AI.';
    case 'agrees':
      return totals.proposals === 0
        ? noReadings
        : `Nothing to confirm here: no undecided agreement has an AI reading that agrees with its type.${amendments}`;
    case 'not_read':
      return 'Every undecided agreement in your scope has an AI reading, or is waiting for one.';
    case 'newer':
      return 'No settled type has a newer AI reading that disagrees with it.';
    case 'amendments':
      return 'No agreement is flagged as possibly an amendment filed on its own.';
  }
}
